import * as ort from 'onnxruntime-web';

/**
 * Preprocess image with letterbox (preserves aspect ratio)
 * @param {string} imageDataUrl - Data URL of the image
 * @param {number} targetSize - Model input size (e.g., 1280)
 * @returns {Promise<{tensor: ort.Tensor, metadata: object}>}
 */
export async function preprocessImageLetterbox(imageDataUrl, targetSize = 1280) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      try {
        const { width: origWidth, height: origHeight } = img;
        console.log(`📐 Original image: ${origWidth}x${origHeight}`);
        
        // Calculate scale to fit within targetSize
        const scale = Math.min(targetSize / origWidth, targetSize / origHeight);
        const newWidth = Math.round(origWidth * scale);
        const newHeight = Math.round(origHeight * scale);
        
        const padX = Math.floor((targetSize - newWidth) / 2);
        const padY = Math.floor((targetSize - newHeight) / 2);
        
        console.log(`  Resized: ${newWidth}x${newHeight}`);
        console.log(`  Padding: x=${padX}, y=${padY}`);
        
        // Create canvas and resize
        const canvas = document.createElement('canvas');
        canvas.width = targetSize;
        canvas.height = targetSize;
        const ctx = canvas.getContext('2d');
        
        // Fill with gray (114, 114, 114)
        ctx.fillStyle = 'rgb(114, 114, 114)';
        ctx.fillRect(0, 0, targetSize, targetSize);
        
        // Draw resized image in center
        ctx.drawImage(img, padX, padY, newWidth, newHeight);
        
        // Get pixel data
        const imageData = ctx.getImageData(0, 0, targetSize, targetSize);
        const { data } = imageData;
        
        // Convert to NCHW Float32Array (normalize to 0-1)
        const floatData = new Float32Array(3 * targetSize * targetSize);
        
        for (let i = 0; i < targetSize * targetSize; i++) {
          const pixelOffset = i * 4; // RGBA
          floatData[i] = data[pixelOffset] / 255.0; // R
          floatData[targetSize * targetSize + i] = data[pixelOffset + 1] / 255.0; // G
          floatData[targetSize * targetSize * 2 + i] = data[pixelOffset + 2] / 255.0; // B
        }
        
        console.log('✅ Image preprocessed to NCHW format');
        console.log('  First 10 values:', Array.from(floatData.slice(0, 10)).map(v => v.toFixed(3)));
        
        // Create ONNX tensor
        const tensor = new ort.Tensor('float32', floatData, [1, 3, targetSize, targetSize]);
        
        const metadata = {
          modelSize: targetSize,
          scale,
          padX,
          padY,
          originalWidth: origWidth,
          originalHeight: origHeight,
        };
        
        resolve({ tensor, metadata });
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    img.src = imageDataUrl;
  });
}

/**
 * Convert detection bbox from letterbox coords to original image coords
 */
export function convertBboxFromLetterbox(bbox, metadata) {
  const { scale, padX, padY } = metadata;
  
  // bbox is in normalized coordinates (0-1) relative to model input
  const modelSize = metadata.modelSize;
  
  // Convert to pixel coordinates in letterboxed image
  const x1Letterbox = bbox.x * modelSize;
  const y1Letterbox = bbox.y * modelSize;
  const x2Letterbox = (bbox.x + bbox.width) * modelSize;
  const y2Letterbox = (bbox.y + bbox.height) * modelSize;
  
  // Remove padding and scale back to original
  const x1 = (x1Letterbox - padX) / scale;
  const y1 = (y1Letterbox - padY) / scale;
  const x2 = (x2Letterbox - padX) / scale;
  const y2 = (y2Letterbox - padY) / scale;
  
  return {
    x: x1,
    y: y1,
    width: x2 - x1,
    height: y2 - y1,
  };
}

